import {
  concatTopology,
  triangulateTopologyForRendering,
} from "../topology.ts";
import type { PolygonTopology, RenderTriangleIndices } from "../types.ts";
import { EMPTY_RENDER_TRIANGLE_INDICES } from "../types.ts";

export type MergeStrategy = "center" | "first" | "last";

/**
 * Concatenate multiple meshes into one. Each mesh provides pre-transformed
 * vertices and its topology. Returns merged geometry with triangulated indices.
 */
export function concatMeshes(
  meshes: Array<{
    vertices: Float32Array;
    topology: PolygonTopology;
  }>,
): {
  vertices: Float32Array;
  indices: RenderTriangleIndices;
  topology: PolygonTopology;
} {
  if (meshes.length === 0) {
    const empty: PolygonTopology = {
      offsets: new Uint32Array([0]),
      corners: new Uint32Array(0),
      edges: new Uint32Array(0),
      cornerEdges: new Uint32Array(0),
      edgeFlags: new Uint8Array(0),
    };
    return {
      vertices: new Float32Array(0),
      indices: EMPTY_RENDER_TRIANGLE_INDICES,
      topology: empty,
    };
  }
  if (meshes.length === 1) {
    const m = meshes[0];
    const indices = triangulateTopologyForRendering(m.topology);
    return {
      vertices: new Float32Array(m.vertices),
      indices,
      topology: m.topology,
    };
  }

  // Merge all vertex arrays
  let totalVerts = 0;
  for (const m of meshes) totalVerts += m.vertices.length;
  const mergedVerts = new Float32Array(totalVerts);

  // Merge topology iteratively
  let vertOffset = 0;
  let mergedTopo = meshes[0].topology;
  mergedVerts.set(meshes[0].vertices);
  vertOffset += meshes[0].vertices.length;

  for (let i = 1; i < meshes.length; i++) {
    const m = meshes[i];
    mergedVerts.set(m.vertices, vertOffset);
    mergedTopo = concatTopology(mergedTopo, m.topology, vertOffset / 3);
    vertOffset += m.vertices.length;
  }

  const indices = triangulateTopologyForRendering(mergedTopo);
  return { vertices: mergedVerts, indices, topology: mergedTopo };
}
